import React from 'react';
import PropTypes from 'prop-types';
import notification from 'cccisd-notification';
import Confirm from 'cccisd-confirm';
import Button from 'cccisd-click-button';
import axios from 'cccisd-axios';
import { updateSessionDeploymentInfo } from 'js/assessment/helpers';
import { Formik, Form, Field, CccisdInput } from 'cccisd-formik';
import style from './style.css';

const Boilerplate = window.cccisd.boilerplate;

const ContactForm = props => {
    const {
        action,
        contactType,
        closeModal,
        session,
        plan,
        refreshContactsData,
        refreshPlanData,
        pawn,
        initialValues,
        contactHasSurveyProgress,
    } = props;

    const updateContact = async values => {
        const updatedPawnValues = {
            contactName: values.name,
            contactEmail: values.email,
            contactType,
        };

        const update = async () => {
            try {
                const response = await axios.put(
                    Boilerplate.route('api.nexus.pawn.update', {
                        pawn: pawn.pawnId,
                    }),
                    {
                        pawnHash: pawn.pawnHash,
                        ...updatedPawnValues,
                    }
                );

                if (response?.data?.status === 'success') {
                    notification({ message: 'Contact updated!', type: 'success' });
                    refreshContactsData();
                    closeModal();
                } else {
                    notification({
                        message: 'An error occured updating contact',
                        type: 'danger',
                    });
                }
            } catch (e) {
                console.error(e);
                notification({
                    message: 'An error occured updating contact',
                    type: 'danger',
                });
            }
        };

        if (contactHasSurveyProgress) {
            Confirm({
                message: 'Confirm Update',
                description:
                    contactType === 'Educator' ? (
                        <>
                            This contact has existing assessment progress. Are you sure you want to update this contact?
                            <br />
                            <br />
                            Note: If you are updating information for a staff position, we recommend creating a new
                            contact.
                        </>
                    ) : (
                        `This contact has existing assessment progress. Are you sure you want to update this contact?`
                    ),
                confirmLabel: 'Update',
                abortLabel: 'Cancel',
            })
                .then(async () => {
                    update();
                })
                .fail(() => {
                    // nothing
                });
        } else {
            update();
        }
    };

    const saveNewContact = async values => {
        const newPawnValues = {
            role: 'contact',
            contactName: values.name,
            contactEmail: values.email,
            contactType,
            parentPawns: plan.selectedByPawn.pawn.pawnId,
        };

        try {
            const response = await axios
                .post(Boilerplate.route('api.nexus.pawn.store'), {
                    ...newPawnValues,
                })
                .then(async addPawnResponse => {
                    await updateSessionDeploymentInfo(
                        [
                            {
                                pawnId: addPawnResponse.data.data.id,
                                respondentHash: addPawnResponse.data.data.respondent_hash,
                            },
                        ],
                        { ...session, assignmentPlanId: plan.assignmentPlanId }
                    );

                    return addPawnResponse;
                });

            if (response?.data?.status === 'success') {
                notification({ message: 'Contact added!', type: 'success' });
                refreshContactsData();
                refreshPlanData();
                closeModal();
            } else {
                notification({ message: 'An error occured adding contact', type: 'danger' });
            }
        } catch (e) {
            console.error(e);
            notification({ message: 'An error occured adding contact', type: 'danger' });
        }
    };

    const onSubmit = async values => {
        if (action === 'add') {
            await saveNewContact(values);
        }
        if (action === 'edit') {
            await updateContact(values);
        }
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={onSubmit}
            render={({ handleSubmit, isSubmitting, values }) => (
                <Form>
                    <label className={style.required}>
                        <b>Name</b>
                    </label>
                    <Field name="name" component={CccisdInput} required />
                    <Field name="email" component={CccisdInput} label="Email" type="email" />
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            marginTop: '1em',
                        }}
                    >
                        <Button
                            onClick={handleSubmit}
                            title="Submit"
                            className="btn btn-primary"
                            isLoading={isSubmitting}
                        />
                    </div>
                </Form>
            )}
        />
    );
};

ContactForm.propTypes = {
    action: PropTypes.string,
    contactType: PropTypes.string,
    closeModal: PropTypes.func,
    session: PropTypes.object,
    plan: PropTypes.object,
    refreshContactsData: PropTypes.func,
    refreshPlanData: PropTypes.func,
    contactHasSurveyProgress: PropTypes.bool,
    pawn: PropTypes.object,
    initialValues: PropTypes.object,
};
ContactForm.defaultProps = {};

export default ContactForm;
